import {missingFromList, recoveryDelay, MISSING_GRACE_MS} from "./chatListCheck";

/*
 * Rebuilding the chat list when it can no longer be trusted, and checking it
 * against the server (utils/chatListCheck has the why). Kept apart from
 * ConversationAction, with its dependencies passed in, so the timing and the
 * failure paths are tested as they run.
 *
 * createChatListRecovery: one rebuild at a time. A rebuild refreshes the
 * sign-in token, then calls `rebuild(reason, details)` (a quiet
 * fetchConversations). The lock is held until that has finished; a rebuild
 * that fails (a read still refused) is logged and tried again later, backing
 * off. `listening` says whether the list's live listeners are attached.
 *
 * createChatListVerifier: compares the list with the server's "open chats
 * assigned to me" and asks for a rebuild when one is missing (after a short
 * grace, for a chat still being delivered), when the query itself fails, or
 * when nothing is missing but the listeners are not attached. One check at a
 * time, and none while a rebuild is pending or running.
 */

const message = (e) => String((e && e.message) || e).slice(0, 200);

export const createChatListRecovery = ({getUser, log, rebuild, setTimer = setTimeout}) => {
    const state = {timer: null, running: false, attempt: 0, listening: false};

    const schedule = (reason, details = {}, {immediate = false} = {}) => {
        const user = getUser();
        if (!user || state.timer || state.running) return false;
        const delayMs = recoveryDelay(state.attempt, immediate);
        log(user.uid, {event: 'chatListRecovering', reason, attempt: state.attempt, delayMs, ...details});
        state.attempt += 1;
        state.timer = setTimer(async () => {
            state.timer = null;
            const current = getUser();
            if (!current) return;
            state.running = true;
            let carried = details;
            try {
                try {
                    await current.getIdToken(true);
                } catch (e) {
                    carried = {...carried, tokenRefreshError: (e && e.code) || 'failed'};
                }
                /* What went wrong travels into the rebuild's own log
                   (chatListLoaded), written once reads work again: logs
                   written while the sign-in lapsed are likely refused. */
                await rebuild(reason, carried);
                state.running = false;
            } catch (e) {
                state.running = false;
                log(current.uid, {event: 'chatListRecoveryFailed', reason, message: message(e)});
                schedule(reason, carried);
            }
        }, delayMs);
        return true;
    };

    return {
        state,
        schedule,
        busy: () => Boolean(state.timer || state.running),
        setListening: (value) => { state.listening = value; },
        /* A clean check: the next failure starts from the shortest wait. */
        settled: () => { state.attempt = 0; },
    };
};

export const createChatListVerifier = ({
    getUser, fetchMyOpenChats, shownIDs, recovery, log,
    graceMs = MISSING_GRACE_MS,
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
}) => {
    let inFlight = false;
    return async (reason, details = {}) => {
        const user = getUser();
        if (!user || inFlight || recovery.busy()) return 'skipped';
        inFlight = true;
        try {
            let serverChats;
            try {
                serverChats = (await fetchMyOpenChats(user.uid)) || {};
            } catch (e) {
                log(user.uid, {event: 'chatListCheckFailed', reason, code: (e && e.code) || null, message: message(e)});
                recovery.schedule('chatListCheckFailed', {checkReason: reason, code: (e && e.code) || null});
                return 'checkFailed';
            }
            let missing = missingFromList(serverChats, shownIDs());
            if (missing.length) {
                await sleep(graceMs);
                const shownNow = shownIDs();
                missing = missing.filter((id) => !shownNow.has(id));
            }
            if (missing.length) {
                log(user.uid, {event: 'chatListMissingChats', reason, count: missing.length, missing: missing.slice(0, 10), ...details});
                recovery.schedule('chatListMissingChats', {checkReason: reason, missingCount: missing.length}, {immediate: true});
                return 'missing';
            }
            /* Nothing missing yet, but nothing would arrive either. */
            if (!recovery.state.listening) {
                recovery.schedule('chatListNotListening', {checkReason: reason}, {immediate: true});
                return 'notListening';
            }
            recovery.settled();
            return 'ok';
        } finally {
            inFlight = false;
        }
    };
};
